import type { BalanceData, AprData, PostTradeMetrics } from '../utils'
import type { LenderYields } from '../../../../types/lenderTypes'
import type { SumerPositionInput } from './types'
import { computePostTradeMetrics } from '../utils'
import {
  getOrganicYieldRepay,
  getRewardYieldRepay,
  getIntrinsicYieldRepay,
} from '../yield'
import {
  computeSumerWaterfall,
  buildSumerAccumulators,
  applyPositionDelta,
} from './waterfall'

/**
 * Compute post-trade balance metrics for a Sumer repay operation.
 *
 * Re-runs the waterfall absorption with decreased debt for the target market.
 * Reducing debt frees collateral from intra-group absorption, potentially
 * increasing the effective collateral valued at inter-group rates.
 */
export function computeSumerRepayDelta(
  amount: number,
  price: number,
  targetMarketUid: string,
  balanceData: BalanceData,
  positions: SumerPositionInput[],
  apr?: AprData,
  yieldParams?: LenderYields,
  irMode?: number,
): PostTradeMetrics {
  const dollarAmount = amount * price
  const mode = irMode ?? 2

  const updatedPositions = applyPositionDelta(
    positions,
    targetMarketUid,
    0,
    mode === 1 ? 0 : -dollarAmount,
    mode === 1 ? -dollarAmount : 0,
  )

  const { groupRatesMap, gAccum, gAccumAll } =
    buildSumerAccumulators(updatedPositions)
  const groups = Object.values(groupRatesMap)
  const waterfallCollateral = computeSumerWaterfall(groups, gAccum)
  const waterfallCollateralAll = computeSumerWaterfall(groups, gAccumAll)

  const newDebt = balanceData.debt - dollarAmount
  const postTrade: BalanceData = {
    ...balanceData,
    borrowDiscountedCollateral: waterfallCollateral,
    borrowDiscountedCollateralAllActive: waterfallCollateralAll,
    collateral: waterfallCollateral,
    collateralAllActive: waterfallCollateralAll,
    deposits: balanceData.deposits,
    debt: newDebt,
    adjustedDebt: Math.max(0, newDebt),
    nav: balanceData.nav + dollarAmount,
  }

  const result = computePostTradeMetrics(balanceData, postTrade)

  if (apr && yieldParams) {
    const cashFlowDeposits = balanceData.deposits * apr.depositApr
    const cashFlowDebt = balanceData.debt * apr.borrowApr
    const rewardFlowDeposits = balanceData.deposits * (apr.rewardDepositApr ?? 0)
    const rewardFlowDebt = balanceData.debt * (apr.rewardBorrowApr ?? 0)
    const intrinsicFlowDeposits = balanceData.deposits * apr.intrinsicDepositApr
    const intrinsicFlowDebt = balanceData.debt * apr.intrinsicBorrowApr

    const newApr = { ...apr }
    ;[newApr.borrowApr, newApr.depositApr, newApr.apr] =
      getOrganicYieldRepay(
        dollarAmount,
        mode,
        cashFlowDeposits,
        cashFlowDebt,
        balanceData.deposits,
        balanceData.debt,
        yieldParams,
      )
    newApr.rewards = getRewardYieldRepay(
      dollarAmount,
      mode,
      rewardFlowDeposits,
      rewardFlowDebt,
      balanceData.deposits,
      balanceData.debt,
      yieldParams,
    )
    ;[
      newApr.intrinsicBorrowApr,
      newApr.intrinsicDepositApr,
      newApr.intrinsicApr,
    ] = getIntrinsicYieldRepay(
      dollarAmount,
      intrinsicFlowDeposits,
      intrinsicFlowDebt,
      balanceData.deposits,
      balanceData.debt,
      yieldParams,
    )
    result.post.aprData = newApr
  }

  return result
}
